import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { Typography } from '@/ui';

import { AssistantPageShell } from './AssistantPageShell';
import { assistantErrorCode, useConfirmAssistantMutation } from './api';

import type { FC } from 'react';

type Confirmation =
  | { state: 'confirming' }
  | { state: 'confirmed' }
  | { code: string | undefined; state: 'failed' };

const RESEND_HINT =
  'Ask the person who added you to resend the confirmation from their Mento settings.';

export const confirmationFailure = (
  code: string | undefined,
): { body: string; title: string } => {
  switch (code) {
    case 'token_expired':
      return {
        body: `This confirmation link has expired. ${RESEND_HINT}`,
        title: 'This link has expired',
      };
    case 'confirmation_stale':
      return {
        body: 'You’re already confirmed, or this invitation was withdrawn. If you can’t sign in, ask the person who added you to check their Mento settings.',
        title: 'This link is no longer needed',
      };
    case 'token_invalid':
    case 'missing':
      return {
        body: `This confirmation link isn't valid or was already used. ${RESEND_HINT}`,
        title: 'This link doesn’t work',
      };
    default:
      return {
        body: 'We couldn’t confirm you just now. Open the link from your email again in a moment.',
        title: 'Something went wrong',
      };
  }
};

/**
 * `/assistant/confirm?token=`: the link a newly added assistant gets by email.
 * Like the sign-in link, the token leaves the URL before it is sent.
 */
const AssistantConfirm: FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const sentToken = useRef<string | null>(null);
  const [confirmation, setConfirmation] = useState<Confirmation>(
    token ? { state: 'confirming' } : { code: 'missing', state: 'failed' },
  );
  const [confirmAssistant] = useConfirmAssistantMutation();

  useEffect(() => {
    if (!token || sentToken.current === token) return;
    sentToken.current = token;
    setConfirmation({ state: 'confirming' });
    navigate('/assistant/confirm', { replace: true });

    confirmAssistant(token)
      .unwrap()
      .then(() => setConfirmation({ state: 'confirmed' }))
      .catch((error: unknown) =>
        setConfirmation({ code: assistantErrorCode(error), state: 'failed' }),
      );
  }, [confirmAssistant, navigate, token]);

  if (confirmation.state === 'confirming') {
    return (
      <AssistantPageShell>
        <Typography variant="body1">Confirming…</Typography>
      </AssistantPageShell>
    );
  }

  if (confirmation.state === 'confirmed') {
    return (
      <AssistantPageShell>
        <div className="flex flex-col gap-3" role="status">
          <Typography variant="h4">You&apos;re confirmed</Typography>
          <Typography variant="body1">
            You&apos;re now a scheduling assistant in Mento. There&apos;s
            nothing else to do: when you need to manage a session, open the link
            in its calendar invite or the{' '}
            <Link className="underline" to="/assistant">
              assistant sign-in page
            </Link>
            , and we&apos;ll email you a sign-in link.
          </Typography>
        </div>
      </AssistantPageShell>
    );
  }

  const { body, title } = confirmationFailure(confirmation.code);
  return (
    <AssistantPageShell>
      <div className="flex flex-col gap-3" role="alert">
        <Typography variant="h4">{title}</Typography>
        <Typography variant="body1">{body}</Typography>
      </div>
    </AssistantPageShell>
  );
};

export default AssistantConfirm;
